import { useTheme } from "@emotion/react";
import {
  Box,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CreditCard, Geo, Person } from "react-bootstrap-icons";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Heading from "../../Components/Heading/Heading";
import DateRangePicker from "../../Components/DateRangePicker/DateRangePicker";
import SiteViewsCounters from "../../Components/SiteViewsCounters/SiteViewsCounters";
import dayjs from "dayjs";
import NoData from "../../Components/NoData/NoData";
import Loading from "../../Components/Loading/Loading";

function Views() {
  const allOption = useMemo(() => ({ id: "all", name: "الكل" }), []);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const [availableData, setAvailableData] = useState({
    sites: [],
    users: [],
    payments: [],
    viewtypes: [],
  });
  const [filters, setFilters] = useState({
    site: { options: [], selected: {} },
    user: { options: [], selected: [] },
    payment: { options: [], selected: [] },
    date: {
      selected: {
        from: new Date(new Date().setDate(new Date().getDate() - 1)),
        to: new Date(new Date().setDate(new Date().getDate())),
      },
    },
  });
  const [data, setData] = useState([]);
  // Relations
  // const [availableViewTypes, setAvailableViewTypes] = useState([]);
  // const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  // const [availableUsers, setAvailableUsers] = useState([]);
  // const [availableSites, setAvailableSites] = useState([]);
  const user = useSelector((state) => state.auth.user);
  const role = useSelector((state) => state.auth.role.roleName);
  const fetchRealtedData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const [viewtype, paymentmethod] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/viewType/`, {
          withCredentials: true,
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/paymentmethod/`, {
          withCredentials: true,
        }),
      ]);

      let users = [];
      let sites = [];
      if (role === "Employee") {
        const userData = await axios.get(
          `${process.env.REACT_APP_API_URL}/user/${user.userId}`,
          {
            withCredentials: true,
          }
        );
        users = [userData.data];

        sites = userData.data?.Sites || [];
      } else {
        users = await axios.get(`${process.env.REACT_APP_API_URL}/user/all`, {
          withCredentials: true,
        });

        sites = await axios.get(`${process.env.REACT_APP_API_URL}/sites`, {
          withCredentials: true,
        });
      }

      setAvailableData({
        users: role !== "Employee" ? users.data : users,
        sites: sites,
        payments: paymentmethod.data,
        viewtypes: viewtype.data,
      });
      setFilters((prev) => ({
        ...prev,
        site: {
          options: [allOption, ...(role !== "Employee" ? sites.data : sites)],
          selected: allOption,
        },
        user: {
          options: [
            ...(role !== "Employee" ? [allOption, ...users.data] : users),
          ],
          selected: [role !== "Employee" ? allOption : users[0]],
        },
        payment: {
          options: [allOption, ...paymentmethod.data],
          selected: [allOption],
        },
      }));
      setInitialLoading(false);
    } catch (error) {
      setInitialLoading(false);
      toast.error("An error occurred");
    }
  }, [user, allOption]);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/view/counters`,
        {
          startDate: dayjs(filters.date.selected.from)
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ss"),

          endDate: dayjs(filters.date.selected.to)
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm:ss"),
          site: filters?.site?.selected?.id,
          users: filters?.user?.selected?.map((u) => u.id),
          payment: filters?.payment?.selected?.map((p) => p.id),
        },
        {
          withCredentials: true,
        }
      );
      if (res) {
        setData(res.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      toast.error(error.message);
    }
  }, [filters]);


  useEffect(() => {
    fetchRealtedData();
  }, [fetchRealtedData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangeSiteFilter = (e) => {
    const selectedSite = e.target.value;

    let siteUsers = [],
      sitePayments = [];
    if (role === "Employee") {
      siteUsers = availableData.users;
      if (selectedSite.id !== "all") {
        sitePayments = [allOption, ...selectedSite.PaymentMethods] || [];
      } else {
        sitePayments = [allOption, ...availableData.payments] || [];
      }
    } else {
      if (selectedSite.id !== "all") {
        siteUsers = [allOption, ...selectedSite.Users] || [];
        sitePayments = [allOption, ...selectedSite.PaymentMethods] || [];
      } else {
        siteUsers = [allOption, ...availableData.users];
        sitePayments = [allOption, ...availableData.payments];
      }
    }

    setFilters((prev) => ({
      ...prev,
      site: { ...prev.site, selected: selectedSite },
      user: {
        options: siteUsers,
        selected: [role === "Employee" ? availableData.users[0] : allOption],
      },
      payment: { options: sitePayments, selected: [allOption] },
    }));
  };
  const onButtonClick = (url) => {
    navigate(url);
  };
  const handleChangeUsersFilter = (e) => {
    let selectedUsers = e.target.value;
    if (selectedUsers[selectedUsers.length - 1]?.name === "الكل") {
      selectedUsers = [allOption];
    } else {
      selectedUsers = selectedUsers.filter((user) => user.name !== "الكل");
    }
    setFilters((prev) => ({
      ...prev,
      user: { ...prev.user, selected: selectedUsers },
    }));
  };
  const handleChangePaymentsFilter = (e) => {
    let selectedPayments = e.target.value;
    if (selectedPayments[selectedPayments.length - 1]?.name === "الكل") {
      selectedPayments = [allOption];
    } else {
      selectedPayments = selectedPayments.filter((p) => p.name !== "الكل");
    }
    if (selectedPayments.length === 0) {
      selectedPayments = [allOption];
    }
    setFilters((prev) => ({
      ...prev,
      payment: { ...prev.payment, selected: selectedPayments },
    }));
  };

  const handleSelectRange = (range) => {
    setFilters({
      ...filters,
      date: {
        selected: range,
      },
    });
  };

  // console.log(`Selected Date`);
  // console.log(filters.date.selected);
  // console.log(filters.date.selected.from.toISOString().split("T")[0]);
  return (
    <div>
      <Heading
        title={"المشاهدات"}
        button={"إضافة مشاهدات جديدة"}
        onButtonClick={() => onButtonClick("/app/views/new")}
        secondaryButton={"كل المشاهدات"}
        onSecondaryButtonClick={() => onButtonClick("/app/views/table")}
      />
      {initialLoading ? (
        <Loading />
      ) : (
        <Box>
          <Box
            sx={{
              display: "flex",
              gap: { xs: "24px", sm: "12px" },
              justifyContent: "end",
              padding: { xs: "12px 20px", sm: "12px 48px" },
              flexWrap: "wrap",
            }}
          >
            <DateRangePicker
              range={filters.date.selected}
              handleSelect={handleSelectRange}
            />
            {filters.site.options.length > 0 ? (
              <FormControl
                sx={{ minWidth: "160px", width: { xs: "100%", sm: "160px" } }}
              >
                <InputLabel>المواقع</InputLabel>
                <Select
                  value={filters.site.selected}
                  label="المواقع"
                  onChange={handleChangeSiteFilter}
                  renderValue={(selected) => (
                    <Box>
                      <Geo />
                      {selected.name}
                    </Box>
                  )}
                >
                  {filters.site.options.map((filter, index) => (
                    <MenuItem key={index} value={filter}>
                      {filter.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Skeleton variant="rounded" width={160} height={56} />
            )}
            {filters.payment.options.length > 0 ? (
              <FormControl
                sx={{ minWidth: "160px", width: { xs: "100%", sm: "160px" } }}
              >
                <InputLabel>طرق الدفع</InputLabel>
                <Select
                  value={filters.payment.selected}
                  multiple={true}
                  label="طرق الدفع"
                  onChange={handleChangePaymentsFilter}
                  renderValue={(value) => {
                    return (
                      <Box display="flex" alignItems="center">
                        <CreditCard />
                        <Box ml={1}>
                          {value?.map((v, i) => v.name).join(",")}
                        </Box>
                      </Box>
                    );
                  }}
                >
                  {filters.payment.options.map((filter, index) => (
                    <MenuItem key={index} value={filter}>
                      {filter.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Skeleton variant="rounded" width={160} height={56} />
            )}
            {filters.user.options.length > 0 ? (
              <FormControl
                sx={{ minWidth: "160px", width: { xs: "100%", sm: "160px" } }}
              >
                <InputLabel id="demo-simple-select-label">الموظفين</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={filters.user.selected}
                  multiple={true}
                  label="الموظفين"
                  onChange={handleChangeUsersFilter}
                  renderValue={(value) => {
                    return (
                      <Box display="flex" alignItems="center">
                        <Person />
                        <Box ml={1}>
                          {value.map((v, i) => v.name).join(",")}
                        </Box>
                      </Box>
                    );
                  }}
                >
                  {filters.user?.options?.map((filter, index) => (
                    <MenuItem key={index} value={filter}>
                      {filter.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Skeleton variant="rounded" width={160} height={56} />
            )}
          </Box>
          {loading ? (
            <Loading />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "28px",
                padding: { xs: "12px", md: "20px", lg: "24px 48px" },
              }}
            >
              {data.length > 0 ? (
                data.map((v, i) => <SiteViewsCounters siteData={v} key={i} />)
              ) : (
                <NoData
                  title={"لا يوجد مشاهدات!"}
                  message={
                    "لا يوجد مشاهدات مناسبة ، يرجى إضافة مشاهدات جديدة أولاً."
                  }
                />
              )}
            </Box>
          )}
        </Box>
      )}
    </div>
  );
}

export default Views;
